/**
 * Remark plugin for GitHub-flavored alert blockquotes.
 *
 * Transforms blockquotes like:
 *   > [!NOTE]
 *   > Content here
 *
 * Into custom `alert` nodes that can be rendered via react-markdown components.
 *
 * Supports: NOTE, TIP, IMPORTANT, WARNING, CAUTION
 */

import type { Root, Blockquote, Paragraph, Text } from "mdast";
import type { Plugin } from "unified";

export type AlertType = "note" | "tip" | "important" | "warning" | "caution";

const ALERT_RE = /^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*/i;

/**
 * Remark plugin that detects GitHub-style alert blockquotes and attaches
 * metadata as `data.hProperties` so react-markdown can pick them up.
 */
export const remarkGithubAlerts: Plugin<[], Root> = () => {
  return (tree) => {
    visit(tree);
  };
};

function visit(node: Root | Blockquote | Paragraph | Text | { type: string; children?: unknown[] }) {
  if (!("children" in node) || !Array.isArray(node.children)) return;

  for (const child of node.children) {
    if (child.type === "blockquote") {
      processBlockquote(child as Blockquote);
    }
    visit(child as Root);
  }
}

function processBlockquote(node: Blockquote) {
  const firstChild = node.children[0];
  if (!firstChild || firstChild.type !== "paragraph") return;

  const paragraph = firstChild as Paragraph;
  const firstInline = paragraph.children[0];
  if (!firstInline || firstInline.type !== "text") return;

  const text = firstInline as Text;
  const match = ALERT_RE.exec(text.value);
  if (!match) return;

  const alertType = match[1]!.toLowerCase() as AlertType;

  // Strip the [!TYPE] prefix from the text
  text.value = text.value.slice(match[0].length);

  // If the text is now empty, remove it
  if (!text.value.trim() && paragraph.children.length > 1) {
    paragraph.children.shift();
    // Also strip leading linebreak if present
    const next = paragraph.children[0];
    if (next && next.type === "text") {
      (next as Text).value = (next as Text).value.replace(/^\n/, "");
    }
  } else if (!text.value.trim() && paragraph.children.length === 1) {
    // Remove the empty paragraph entirely
    node.children.shift();
  }

  // Attach alert metadata via data.hProperties for react-markdown
  (node as any).data = {
    ...(node as any).data,
    hProperties: {
      ...((node as any).data?.hProperties ?? {}),
      "data-alert-type": alertType,
    },
  };
}
